// F-083 — Bibliothèque de l'espace, recherche, partages explicites (bail, propriétaire) et accès locataire.
import { useState } from "react";
import { useParams } from "react-router";
import { del, post, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateLongFmt, dateTime, fileSize } from "@core/format";
import { PublicShell } from "@core/layout";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, FileLine, Input, KeyValues, Loading, Modal, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { BuildingSelect, DocLink, LeaseSelect, PropertySelect, orgApi, useAction, useRefs } from "./shared";

type LibItem = {
  id: string;
  title: string;
  filename: string;
  mime: string;
  size_bytes: number;
  category: string;
  status: string;
  version: number;
  property_id: string | null;
  property_label: string | null;
  lease_id: string | null;
  doc_date: string;
  issuer: string | null;
  created_at: string;
  active_shares: number;
  active_links: number;
  operation_id: string | null;
};

export const DOC_CATEGORIES: Record<string, string> = {
  facture: "Facture",
  justificatif: "Justificatif",
  taxe_fonciere: "Taxe foncière",
  appel_de_fonds: "Appel de fonds",
  decompte_charges: "Décompte de charges",
  convocation_ag: "Convocation d’AG",
  proces_verbal_ag: "Procès-verbal d’AG",
  contrat_fournisseur: "Contrat fournisseur",
  attestation_assurance: "Attestation d’assurance",
  releve_compteur: "Relevé de compteur",
  quittance: "Quittance ou reçu",
  courrier: "Courrier",
  boite_reception: "À classer",
  reglement: "Règlement",
  autre: "Autre",
};

const catLabel = (c: string) => DOC_CATEGORIES[c] ?? c.replace(/_/g, " ");

export function DocumentsPage() {
  const org = useOrg();
  const refs = useRefs();
  const [f, setF] = useState({ q: "", category: "", property_id: "", building_id: "", lease_id: "", from: "", to: "", replaced: false });
  const params = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (k === "replaced") {
      if (v) params.set("include_replaced", "true");
    } else if (v) params.set(k, String(v));
  });
  const q = useGet<{ items: LibItem[]; categories: { category: string; count: number }[] }>(org.api(`/finance/library?${params}`));
  const [open, setOpen] = useState<LibItem | null>(null);
  const [adding, setAdding] = useState(false);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Documents"
        intro="Toutes les pièces de l’espace, hors dossiers de candidature. La recherche ne montre que ce que votre rôle permet de consulter ; un partage se fait pièce par pièce et peut être retiré."
        actions={org.can("operate") ? <Button onClick={() => setAdding(true)}>Ajouter un document</Button> : null}
      />
      <Card>
        <div className="finances-recherche">
          <Input label="Rechercher" type="search" placeholder="Titre, fichier, émetteur, logement" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
          <Select label="Catégorie" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} options={[{ value: "", label: "Toutes" }, ...(q.data?.categories ?? []).map((c) => ({ value: c.category, label: `${catLabel(c.category)} (${c.count})` }))]} />
          <PropertySelect refs={refs.data} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} empty="Tous" />
          <BuildingSelect refs={refs.data} value={f.building_id} onChange={(v) => setF({ ...f, building_id: v })} empty="Tous" />
          <LeaseSelect refs={refs.data} value={f.lease_id} onChange={(v) => setF({ ...f, lease_id: v })} empty="Tous" />
          <Input label="Du" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
          <Input label="Au" type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
          <Checkbox label="Inclure les versions remplacées" checked={f.replaced} onChange={(v) => setF({ ...f, replaced: v })} />
        </div>
      </Card>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun document ne correspond." /> : null}
      {q.data && q.data.items.length ? (
        <Table caption="Documents">
          <thead>
            <tr>
              <th>Document</th>
              <th>Catégorie</th>
              <th>Logement</th>
              <th>Date</th>
              <th>Accès</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((d) => (
              <tr key={d.id}>
                <td>
                  <DocLink id={d.id} name={d.title || d.filename} />
                  <span className="finances-sous">
                    {d.filename} · {fileSize(d.size_bytes)}
                    {d.issuer ? ` · ${d.issuer}` : ""}
                  </span>
                </td>
                <td>{catLabel(d.category)}</td>
                <td>{d.property_label ?? "—"}</td>
                <td className="num">{date(d.doc_date)}</td>
                <td>
                  <div className="row">
                    {d.status === "remplace" ? <Badge tone="attention">Version remplacée</Badge> : null}
                    {d.lease_id ? <Badge tone="encre">Rattachée au bail</Badge> : null}
                    {d.active_shares ? <Badge tone="encre">Partagée ({d.active_shares})</Badge> : null}
                    {d.active_links ? <Badge tone="encre">Lien actif ({d.active_links})</Badge> : null}
                    {d.operation_id ? <Badge tone="succes">Justifie une opération</Badge> : null}
                  </div>
                </td>
                <td>
                  <Button variant="discret" small onClick={() => setOpen(d)}>
                    Détails et partage
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {open ? <DocumentModal orgId={org.orgId} docId={open.id} onClose={() => setOpen(null)} /> : null}
      {adding ? <AddDocumentModal onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function AddDocumentModal({ onClose }: { onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const [f, setF] = useState({ title: "", category: "autre", property_id: "", lease_id: "" });
  const a = useAction();
  return (
    <Modal open onClose={onClose} title="Ajouter un document">
      <div className="stack">
        <Input label="Titre" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div className="form-row">
          <Select label="Catégorie" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} options={Object.entries(DOC_CATEGORIES).filter(([k]) => k !== "boite_reception").map(([value, label]) => ({ value, label }))} />
          <PropertySelect refs={refs.data} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, lease_id: "" })} empty="Aucun logement" />
        </div>
        {f.property_id ? <LeaseSelect refs={refs.data} propertyId={f.property_id} value={f.lease_id} onChange={(v) => setF({ ...f, lease_id: v })} empty="Ne pas rattacher au bail" help="Rattachée au bail, la pièce devient consultable par ses locataires." /> : null}
        <ErrorBox error={a.error} />
        <FileDrop
          busy={a.busy}
          onFiles={async (files) => {
            const form = new FormData();
            form.append("file", files[0]);
            form.append("title", f.title);
            form.append("category", f.category);
            if (f.property_id) form.append("property_id", f.property_id);
            if (f.lease_id) form.append("lease_id", f.lease_id);
            const r = await a.run(() => upload(org.api("/documents"), form), { success: "Document ajouté.", refresh: [org.api("/finance/library")] });
            if (r) onClose();
          }}
        />
      </div>
    </Modal>
  );
}

type Detail = {
  document: { id: string; title: string; filename: string; category: string; status: string; version: number; lease_id: string | null; created_at: string };
  shares: { id: string; lease_id: string; property_label: string; tenants: string; message: string; created_at: string; revoked_at: string | null }[];
  links: { id: string; owner_name: string; expires_at: string; revoked_at: string | null; access_count: number; last_accessed_at: string | null; created_at: string }[];
  versions: { id: string; version: number; status: string; filename: string; created_at: string }[];
  operations: { id: string; label: string; date: string; amount_cents: number }[];
  visible_to_lease_parties: boolean;
};

function DocumentModal({ orgId, docId, onClose }: { orgId: string; docId: string; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs(orgId);
  const q = useGet<Detail>(orgApi(orgId, `/finance/library/${docId}`));
  const [share, setShare] = useState({ lease_id: "", message: "", notify: true });
  const [link, setLink] = useState({ owner_id: "", days: "7", message: "" });
  const a = useAction();
  const canShare = org.can("operate");
  const refresh = [orgApi(orgId, "/finance/library")];
  const d = q.data;
  return (
    <Modal open onClose={onClose} title="Document">
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {d ? (
          <>
            <FileLine name={d.document.title || d.document.filename} href={`/api/v1/documents/${d.document.id}/fichier`} />
            {d.document.status === "remplace" ? <Notice tone="attention">Cette version a été remplacée : consultez la plus récente.</Notice> : null}
            {d.visible_to_lease_parties ? <Notice>Rattachée à un bail : ses locataires peuvent la consulter.</Notice> : null}
            {d.versions.length > 1 ? (
              <KeyValues items={d.versions.map((v) => [`Version ${v.version}`, <><DocLink id={v.id} name={v.filename} /> · {dateTime(v.created_at)} {v.status === "remplace" ? <Badge>Remplacée</Badge> : null}</>])} />
            ) : null}
            {d.operations.length ? (
              <p className="small">
                Justifie : {d.operations.map((o) => `« ${o.label} » du ${date(o.date)}`).join(", ")}
              </p>
            ) : null}
            <h3>Partages avec des locataires</h3>
            {d.shares.length === 0 ? <p className="muted small">Aucun partage.</p> : null}
            <ul className="list-plain">
              {d.shares.map((s) => (
                <li key={s.id} className="row-between">
                  <span>
                    {s.property_label}
                    {s.tenants ? ` — ${s.tenants}` : ""} <span className="finances-sous">le {dateTime(s.created_at)}</span>
                  </span>
                  {s.revoked_at ? (
                    <Badge>Retiré le {date(s.revoked_at)}</Badge>
                  ) : canShare ? (
                    <Button variant="discret" small busy={a.busy} onClick={() => a.run(() => del(orgApi(orgId, `/finance/shares/${s.id}`)), { success: "Partage retiré.", refresh })}>
                      Retirer le partage
                    </Button>
                  ) : (
                    <Badge tone="encre">Actif</Badge>
                  )}
                </li>
              ))}
            </ul>
            {canShare ? (
              <div className="stack-sm">
                <LeaseSelect refs={refs.data} value={share.lease_id} onChange={(v) => setShare({ ...share, lease_id: v })} label="Partager avec les locataires du bail" empty="Choisir un bail…" />
                <Textarea label="Message (facultatif)" value={share.message} onChange={(e) => setShare({ ...share, message: e.target.value })} />
                <Checkbox label="Prévenir chaque locataire par un courriel individuel" checked={share.notify} onChange={(v) => setShare({ ...share, notify: v })} />
                <div>
                  <Button disabled={!share.lease_id} busy={a.busy} onClick={() => a.run(() => post(orgApi(orgId, `/finance/library/${docId}/shares`), share), { success: "Document partagé.", refresh }).then((r) => r && setShare({ lease_id: "", message: "", notify: true }))}>
                    Partager
                  </Button>
                </div>
              </div>
            ) : null}
            <h3>Liens temporaires envoyés à un propriétaire</h3>
            {d.links.length === 0 ? <p className="muted small">Aucun lien.</p> : null}
            <ul className="list-plain">
              {d.links.map((l) => {
                const expired = new Date(l.expires_at) < new Date();
                return (
                  <li key={l.id} className="row-between">
                    <span>
                      {l.owner_name} <span className="finances-sous">valable jusqu’au {dateTime(l.expires_at)} · ouvert {l.access_count} fois</span>
                    </span>
                    {l.revoked_at ? (
                      <Badge>Retiré</Badge>
                    ) : expired ? (
                      <Badge>Expiré</Badge>
                    ) : canShare ? (
                      <Button variant="discret" small onClick={() => a.run(() => del(orgApi(orgId, `/finance/links/${l.id}`)), { success: "Lien retiré.", refresh })}>
                        Retirer le lien
                      </Button>
                    ) : (
                      <Badge tone="encre">Actif</Badge>
                    )}
                  </li>
                );
              })}
            </ul>
            {canShare && (refs.data?.owners.length ?? 0) > 0 ? (
              <div className="stack-sm">
                <div className="form-row">
                  <Select
                    label="Propriétaire"
                    value={link.owner_id}
                    onChange={(e) => setLink({ ...link, owner_id: e.target.value })}
                    options={[{ value: "", label: "Choisir…" }, ...(refs.data?.owners ?? []).map((o) => ({ value: o.id, label: `${o.display_name}${o.has_email ? "" : " (sans adresse e-mail)"}` }))]}
                  />
                  <Select label="Durée de validité" value={link.days} onChange={(e) => setLink({ ...link, days: e.target.value })} options={["1", "7", "14", "30"].map((d) => ({ value: d, label: `${d} jour${d === "1" ? "" : "s"}` }))} />
                </div>
                <Textarea label="Message (facultatif)" value={link.message} onChange={(e) => setLink({ ...link, message: e.target.value })} />
                <ErrorBox error={fieldError(a.error, "owner_id") ? a.error : null} />
                <div>
                  <Button variant="secondaire" disabled={!link.owner_id} busy={a.busy} onClick={() => a.run(() => post(orgApi(orgId, `/finance/library/${docId}/links`), { ...link, days: Number(link.days) }), { success: "Lien envoyé par courriel.", refresh })}>
                    Envoyer un lien temporaire
                  </Button>
                </div>
              </div>
            ) : null}
            <ErrorBox error={a.error && !fieldError(a.error, "owner_id") ? a.error : null} />
          </>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Onglet « Documents partagés » du bail (côté bailleur)

export function LeaseSharesTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const org = useOrg();
  const q = useGet<{ shares: (Detail["shares"][number] & { document_id: string; document_title: string; document_filename: string })[]; attached: { id: string; title: string; filename: string; category: string; visible_locataire?: boolean }[]; note: string }>(orgApi(orgId, `/finance/leases/${leaseId}/shares`));
  const [search, setSearch] = useState("");
  const lib = useGet<{ items: LibItem[] }>(search.trim().length >= 2 ? orgApi(orgId, `/finance/library?q=${encodeURIComponent(search.trim())}`) : null);
  const a = useAction();
  const refresh = [orgApi(orgId, `/finance/leases/${leaseId}`), orgApi(orgId, "/finance/library")];
  const canShare = org.can("operate");
  const active = (q.data?.shares ?? []).filter((s) => !s.revoked_at);
  return (
    <div className="stack">
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? a.error} />
      {q.data ? <Notice>{q.data.note}</Notice> : null}
      <Card title="Partagés avec les locataires">
        {active.length === 0 ? <p className="muted">Aucun document partagé.</p> : null}
        <ul className="list-plain">
          {active.map((s) => (
            <li key={s.id} className="row-between">
              <span>
                <DocLink id={s.document_id} name={s.document_title || s.document_filename} /> <span className="finances-sous">partagé le {dateTime(s.created_at)}</span>
              </span>
              {canShare ? (
                <Button variant="discret" small onClick={() => a.run(() => del(orgApi(orgId, `/finance/shares/${s.id}`)), { success: "Partage retiré.", refresh })}>
                  Retirer
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {canShare ? (
          <div className="stack-sm">
            <Input label="Partager un document de la bibliothèque" type="search" placeholder="Tapez au moins deux lettres du titre" value={search} onChange={(e) => setSearch(e.target.value)} />
            {(lib.data?.items ?? []).slice(0, 8).map((d) => (
              <div key={d.id} className="row-between">
                <span>
                  {d.title || d.filename} <span className="finances-sous">{catLabel(d.category)}</span>
                </span>
                <Button small variant="secondaire" busy={a.busy} onClick={() => a.run(() => post(orgApi(orgId, `/finance/library/${d.id}/shares`), { lease_id: leaseId, notify: true }), { success: "Document partagé, locataires prévenus.", refresh })}>
                  Partager
                </Button>
              </div>
            ))}
          </div>
        ) : null}
      </Card>
      <Card title="Rattachés au bail">
        {(q.data?.attached ?? []).length === 0 ? <p className="muted">Aucune pièce rattachée directement au bail.</p> : null}
        <ul className="list-plain">
          {(q.data?.attached ?? []).map((d) => (
            <li key={d.id}>
              <DocLink id={d.id} name={d.title || d.filename} /> <span className="finances-sous">{catLabel(d.category)}</span>{" "}
              <span className="finances-sous">{d.visible_locataire ? "· visible par le locataire" : "· réservé au bailleur"}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section « Documents partagés » côté locataire

export function TenantSharedSection({ leaseId }: { leaseId: string }) {
  const q = useGet<{ items: { share_id: string; document_id: string; title: string; filename: string; size_bytes: number; category: string; message: string; shared_at: string }[] }>(`/api/v1/tenant-documents/${leaseId}`);
  return (
    <div className="stack">
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun document partagé par votre bailleur pour le moment." /> : null}
      <ul className="list-plain">
        {(q.data?.items ?? []).map((d) => (
          <li key={d.share_id}>
            <FileLine name={d.title || d.filename} size={d.size_bytes} href={`/api/v1/tenant-documents/${leaseId}/${d.document_id}/file`} right={<span className="small muted">partagé le {date(d.shared_at)}</span>} />
            {d.message ? <p className="small muted">{d.message}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page publique d'un lien temporaire

export function PublicSharePage() {
  const { token = "" } = useParams();
  const q = useGet<{ title: string; filename: string; size_bytes: number; expires_at: string; sender: string }>(`/api/v1/document-links/${encodeURIComponent(token)}`);
  return (
    <PublicShell>
      <h1>Document transmis</h1>
      {q.isLoading ? <Loading /> : null}
      {q.error ? (
        <Notice tone="attention" title="Lien indisponible">
          Ce lien est introuvable, a expiré ou a été retiré. Demandez un nouveau lien à l’expéditeur.
        </Notice>
      ) : null}
      {q.data ? (
        <div className="stack">
          <p>
            <strong>{q.data.sender}</strong> vous transmet ce document. Le lien est personnel et valable jusqu’au {dateLongFmt(q.data.expires_at)}.
          </p>
          <FileLine name={q.data.title} size={q.data.size_bytes} href={`/api/v1/document-links/${encodeURIComponent(token)}/file`} />
          <p className="small muted">Chaque ouverture est enregistrée. Ne transférez pas ce lien.</p>
        </div>
      ) : null}
    </PublicShell>
  );
}
